import axios from 'config/axios';

/**
 * Sentral student SSO routing: lsgo-authentication for schools migrated to
 * it, legacy SSO for everyone else. Ported from lsgo-teacher's util of the
 * same name, without its SIS-iframe testing override (no iframe sign-in here).
 *
 * Legacy's public sis/sso/endpoint names the LSGO school and its sign-in mode,
 * `authMode: 'legacy' | 'auth'`. Only 'auth' moves a school to
 * lsgo-authentication; anything unknown (no school id, lookup failed, any
 * other authMode) stays on legacy.
 */

const CACHE_PREFIX = 'lsgo_sis_school:';
// Legacy's answer is reused for at most this long, so a cutover or rollback
// reaches open tabs within a minute.
const CACHE_TTL_MS = 60 * 1000;

async function lookupSchool(sisSchoolId) {
    const key = `${CACHE_PREFIX}${sisSchoolId}`;
    try {
        const cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
            return { tenant: cached.tenant, authMode: cached.authMode };
        }
    } catch {
        // Storage unavailable: look it up.
    }
    // ?student=true as SentralLogin's own endpoint lookup sends it.
    const { data } = await axios.get(
        `/api/v4/schools/sis/sso/endpoint/${encodeURIComponent(sisSchoolId)}?student=true`
    );
    const school = {
        tenant: data?.schoolId ? `lsgo_school_${data.schoolId}` : null,
        authMode: data?.authMode ?? 'legacy',
    };
    try {
        if (school.tenant) window.sessionStorage.setItem(key, JSON.stringify({ ...school, fetchedAt: Date.now() }));
    } catch {
        // Storage unavailable: not cached.
    }
    return school;
}

/** { tenant, useLsgoAuth } for an SIS school id. Never throws. */
export async function resolveSisSchool(sisSchoolId) {
    if (!sisSchoolId) return { tenant: null, useLsgoAuth: false };
    try {
        const { tenant, authMode } = await lookupSchool(sisSchoolId);
        return { tenant, useLsgoAuth: Boolean(tenant) && authMode === 'auth' };
    } catch {
        return { tenant: null, useLsgoAuth: false };
    }
}
